/**
 * Utility function to merge CSS classes
 * Similar to clsx/classnames functionality
 */
export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Merges Tailwind CSS classes, removing duplicates and resolving conflicts
 * Prioritizes classes from the second argument (conditional formatting) over existing classes
 */
export function mergeTailwindClasses(existing: string, additional: string): string {
  if (!existing) return additional;
  if (!additional) return existing;
  
  // Split into arrays
  const existingClasses = existing.split(/\s+/).filter(Boolean);
  const additionalClasses = additional.split(/\s+/).filter(Boolean);
  
  // Track conflicting utility classes (bg-*, text-*, font-*)
  const conflicts = new Map<string, string>();
  
  // Process existing classes
  existingClasses.forEach(cls => {
    const conflictKey = getConflictKey(cls);
    if (conflictKey) {
      conflicts.set(conflictKey, cls);
    }
  });
  
  // Override with additional classes (conditional formatting takes priority)
  additionalClasses.forEach(cls => {
    const conflictKey = getConflictKey(cls);
    if (conflictKey) {
      conflicts.set(conflictKey, cls);
    }
  });
  
  // Build final class list
  const finalClasses = new Set<string>();
  
  // Add existing classes that don't conflict
  existingClasses.forEach(cls => {
    const conflictKey = getConflictKey(cls);
    if (!conflictKey || conflicts.get(conflictKey) === cls) {
      finalClasses.add(cls);
    }
  });
  
  // Add additional classes (they override conflicting ones)
  additionalClasses.forEach(cls => {
    finalClasses.add(cls);
  });
  
  return Array.from(finalClasses).join(' ').trim();
}

/**
 * Gets a conflict key for a Tailwind class
 * Classes with the same conflict key will override each other
 */
function getConflictKey(className: string): string | null {
  // Font weight classes conflict with each other
  if (className.match(/^font-(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)$/)) {
    return 'font-weight';
  }
  // Background color classes conflict
  if (className.match(/^bg-/)) {
    return 'bg';
  }
  // Text color classes conflict
  if (className.match(/^text-/)) {
    return 'text';
  }
  // Border color classes conflict
  if (className.match(/^border-/)) {
    return 'border';
  }
  return null;
}

/**
 * Creates a variant system for components similar to class-variance-authority
 */
export function createVariants(
  base: string,
  variants: Record<string, Record<string, string>>,
  defaultVariants: Record<string, string> = {}
) {
  return function (props: Record<string, any> = {}) {
    let result = base;

    Object.keys(variants).forEach((variantKey) => {
      const variantValue = props[variantKey] || defaultVariants[variantKey];
      if (variantValue && variants[variantKey][variantValue]) {
        result += ' ' + variants[variantKey][variantValue];
      }
    });

    if (props.className) {
      result += ' ' + props.className;
    }

    return result.replace(/\s+/g, ' ').trim();
  };
}

